import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWA, useLoad, useDebounced, useSelection, PageHead, Card, Search, Btn, Drawer, Field, ImageInput, BulkBar, Check, Pager, Skeleton, Empty, ErrorBox, num, ago, fdt, cx } from "../ui";
import { absUrl, siteLink } from "../api";

const TAKE = 40;

function PosterDrawer({ id, onClose, onChanged }) {
  const { api, run, confirm } = useWA();
  const [p, setP] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("posterGet", { id }).then((r) => {
      setP(r.poster);
      setF({ ...r.poster, hashtags: (r.poster.hashtags || []).join(", "), keywords: (r.poster.keywords || []).join(", "), altText: r.poster.altText || "", category: r.poster.category || "" });
    }).catch(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await run("posterSave", { id, title: f.title, caption: f.caption, altText: f.altText, category: f.category, hashtags: f.hashtags, keywords: f.keywords, image: f.image, ratio: f.ratio }, "Poster save हो गया");
      onChanged();
    } catch (e) {
      /* toast */
    }
    setBusy(false);
  };
  const del = async () => {
    if (!(await confirm({ title: "यह poster delete करें?", danger: true }))) return;
    try {
      await run("posterDelete", { ids: [id] }, "Poster delete हो गया");
      onChanged(true);
    } catch (e) {
      /* toast */
    }
  };
  return (
    <Drawer open onClose={onClose} title={p ? p.title : "Poster"} subtitle={p && <>{p.author ? p.author.name || p.author.email : ""} · {fdt(p.createdAt)} · {num(p.views)} views · {num(p.likes)} likes</>}
      footer={f && <>
        <Btn className="!text-rose-600 mr-auto" icon="fa-regular fa-trash-can" onClick={del}>Delete</Btn>
        <a href={siteLink(`/posters/${p.slug}`)} target="_blank" rel="noreferrer"><Btn icon="fa-solid fa-arrow-up-right-from-square">Site पर</Btn></a>
        <Btn variant="primary" loading={busy} onClick={save}>Save</Btn>
      </>}>
      {!f ? <Skeleton rows={6} /> : (
        <div className="space-y-4">
          <img src={absUrl(f.image)} alt="" className="w-full max-h-[340px] object-contain rounded-2xl border wa-line" style={{ background: "color-mix(in srgb, var(--wa-ink) 4%, transparent)" }} />
          <ImageInput label="Image बदलें" value={f.image} folder="posters" onChange={(v) => set("image", v)} />
          <Field label="Title"><input className="wa-input" value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Caption"><textarea rows={5} className="wa-input" value={f.caption} onChange={(e) => set("caption", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><input className="wa-input" value={f.category} onChange={(e) => set("category", e.target.value)} /></Field>
            <Field label="Ratio">
              <select className="wa-input" value={f.ratio} onChange={(e) => set("ratio", e.target.value)}>
                {["1:1", "4:5", "16:9", "9:16"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Hashtags" hint="Comma से अलग"><input className="wa-input" value={f.hashtags} onChange={(e) => set("hashtags", e.target.value)} /></Field>
          <Field label="Keywords" hint="Comma से अलग"><input className="wa-input" value={f.keywords} onChange={(e) => set("keywords", e.target.value)} /></Field>
          <Field label="Alt text"><input className="wa-input" value={f.altText} onChange={(e) => set("altText", e.target.value)} /></Field>
        </div>
      )}
    </Drawer>
  );
}

export default function Posters() {
  const { run, confirm } = useWA();
  const loc = useLocation();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("new");
  const [skip, setSkip] = useState(0);
  const dq = useDebounced(q);
  const open = new URLSearchParams(loc.search).get("open");
  useEffect(() => setSkip(0), [dq, cat, sort]);
  const { data, loading, error, reload } = useLoad("posters", { q: dq, category: cat, sort, take: TAKE, skip }, [dq, cat, sort, skip]);
  const items = (data && data.items) || [];
  const S = useSelection(items);

  const bulkDelete = async () => {
    if (!(await confirm({ title: `${S.sel.length} posters delete करें?`, danger: true }))) return;
    try {
      await run("posterDelete", { ids: S.sel }, "Posters delete हो गए");
      reload();
    } catch (e) {
      /* toast */
    }
  };

  return (
    <div>
      <PageHead icon="fa-regular fa-images" title="Posters" subtitle={data ? `कुल ${num(data.total)} posters` : ""} />
      <div className="flex flex-wrap gap-2 mb-4">
        <Search value={q} onChange={setQ} placeholder="Title या caption खोजें…" className="flex-1 min-w-[220px]" />
        <select className="wa-input !w-auto" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
          <option value="">सभी categories</option>
          {((data && data.categories) || []).map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
        </select>
        <select className="wa-input !w-auto" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
          <option value="new">नए पहले</option>
          <option value="views">ज़्यादा views</option>
          <option value="old">पुराने पहले</option>
        </select>
        <Btn icon={S.all ? "fa-solid fa-square-check" : "fa-regular fa-square"} onClick={S.toggleAll} disabled={!items.length}>सब चुनें</Btn>
      </div>
      <BulkBar count={S.sel.length} onClear={S.clear}>
        <Btn size="sm" variant="danger" icon="fa-regular fa-trash-can" onClick={bulkDelete}>Delete</Btn>
      </BulkBar>
      <ErrorBox msg={error} retry={reload} />
      {loading && !data ? <Skeleton rows={4} h={160} /> : !items.length ? <Card><Empty icon="fa-regular fa-images" title="कोई poster नहीं मिला" /></Card> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
            {items.map((p) => (
              <div key={p.id} className={cx("wa-card overflow-hidden group relative", S.has(p.id) && "ring-2")} style={S.has(p.id) ? { "--tw-ring-color": "var(--wa-accent)" } : {}}>
                <button type="button" onClick={() => nav(`/website/posters?open=${p.id}`)} className="block w-full">
                  <img src={absUrl(p.image)} alt="" loading="lazy" className="w-full aspect-square object-cover transition-transform group-hover:scale-[1.03]" />
                </button>
                <span className="absolute top-2 left-2 bg-white/90 rounded-md p-1 shadow"><Check checked={S.has(p.id)} onChange={() => S.toggle(p.id)} /></span>
                <div className="p-3">
                  <p className="font-semibold text-[13.5px] truncate">{p.title}</p>
                  <p className="text-[11.5px] wa-muted truncate">{p.author} · {ago(p.at)}</p>
                  <p className="text-[11.5px] wa-muted mt-1"><i className="fa-regular fa-eye" /> {num(p.views)} · <i className="fa-regular fa-heart" /> {num(p.likes)}{p.category ? ` · ${p.category}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
          <Card pad={false} className="mt-4"><Pager total={data.total} take={TAKE} skip={skip} onChange={setSkip} /></Card>
        </>
      )}
      {open && <PosterDrawer id={open} onClose={() => nav("/website/posters")} onChanged={(gone) => { reload(); if (gone) nav("/website/posters"); }} />}
      <p className="text-xs wa-muted mt-4">Tip: Poster पर click करके उसे edit करें। <Link to="/website/comments?tab=likes&kind=poster" className="underline">Poster likes देखें</Link></p>
    </div>
  );
}
